import { debounce } from 'lodash';
import { useCallback } from 'react';
import { Config } from '../helpers/config';
import {
  AddressComponentsEntity,
  GoogleApiPlacesResponse,
  ResultsEntity,
} from '../models';

declare const window: any;

const key = Config.googleApiKey;

type OnPlacesResultCallback = (res: GoogleApiPlacesResponse) => void;
type OnGeocoderResultCallback = (res: ResultsEntity[]) => void;

export type AddressParts = {
  formattedAddress: string;
  street: string;
  streetNumber: string;
  zipCode: string;
  aal1: string;
  aal2: string;
  aal3: string;
  country: string;
  lat: number;
  lng: number;
};

export type GeocoderSearchParams =
  | { placeId: string }
  | { location: { lat: number; lng: number } };

export type GetGeocoderType = (
  searchParams: GeocoderSearchParams,
  onResult: OnGeocoderResultCallback
) => void;

type SplitAddressFunction = (result: ResultsEntity) => AddressParts;

function getGeocoderData(components: AddressComponentsEntity[], key: string) {
  const currentComponent: AddressComponentsEntity | undefined = components.find(
    (c: AddressComponentsEntity) => {
      return c.types?.includes(key);
    }
  );
  const keyValue =
    key === 'administrative_area_level_2' || key === 'country'
      ? 'short_name'
      : 'long_name';
  return currentComponent ? currentComponent[keyValue] : '';
}

export const loadMaps = async (callback?: () => void) => {
  if (!window.google || (window.google && !window.google.maps)) {
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&v=3.exp&libraries=geometry,drawing,places&language=i&callback=initMap`;
    script.async = true;

    window.initMap = function () {
      callback?.();
    };

    // Append the 'script' element to 'head'
    document.head.appendChild(script);
  }
};

export const useGoogleApiDesktop = () => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getPlaces = useCallback(
    debounce(
      (
        text: string,
        onResult: OnPlacesResultCallback,
        types: string[] = ['address']
      ) => {
        const autocompleteService =
          new window.google.maps.places.AutocompleteService();
        autocompleteService
          ?.getPlacePredictions({
            input: text,
            types: types,
            componentRestrictions: {country: 'it'} 
          })
          ?.then(onResult);
      },
      500
    ),
    []
  );

  const getGeocoder: GetGeocoderType = useCallback(
    (
      searchParams: GeocoderSearchParams,
      onResult: OnGeocoderResultCallback
    ) => {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode(searchParams, onResult);
    },
    []
  );

  const splitAddress: SplitAddressFunction = useCallback(
    (result: ResultsEntity) => {
      const street: string = getGeocoderData(
        result!.address_components!,
        'route'
      );
      const streetNumber: string = getGeocoderData(
        result!.address_components!,
        'street_number'
      );
      const zipCode: string = getGeocoderData(
        result!.address_components!,
        'postal_code'
      );
      const aal1: string = getGeocoderData(
        result!.address_components!,
        'administrative_area_level_1'
      );
      const aal2: string = getGeocoderData(
        result!.address_components!,
        'administrative_area_level_2'
      );
      const aal3: string = getGeocoderData(
        result!.address_components!,
        'administrative_area_level_3'
      );
      const country: string = getGeocoderData(
        result!.address_components!,
        'country'
      );
      const lat = result!.geometry.location.lat();
      const lng = result!.geometry.location.lng();

      return {
        street,
        streetNumber,
        zipCode,
        aal1,
        aal2,
        aal3,
        country,
        lat,
        lng,
        formattedAddress: result.formatted_address,
      };
    },
    []
  );

  return { getPlaces, getGeocoder, splitAddress };
};
